import * as React from 'react';
import { ActionButton, ContextualMenu, ContextualMenuItemType, Dropdown, DropdownMenuItemType, FontIcon, IContextualMenuItem, IDropdownOption, IPersonaProps, Label, Persona, PersonaSize, Stack, Text, Toggle, TooltipHost } from '@fluentui/react';
import { useNavigate } from 'react-router-dom';
import { GraphContext, TenantContext, UserContext } from '../../contexts/ContextWrapper';
import { DomainItem } from '../../interfaces/DomainItem';
import { UserAdminItem } from '../../interfaces/UserAdminItem';
import { updateUser } from '../../helpers/PostToAdminApi';
import { AuthContext } from '../../contexts/AuthContext';
import SettingsShort from '../../interfaces/SettingsShort';
import { GetDomainExtensions } from '../../helpers/GetFromRestApi';
import { GetUseAnyGroups } from '../../helpers/SettingsCompany';
import { PeoplePickerGroups } from '../../helpers/PeoplePicker';

interface HeaderMenuProps {
	logout: () => void
}

const MENU_MARGIN = { root: { margin: 5 } }

const HeaderMenu: React.FC<HeaderMenuProps> = (props) => {

	const [showContextualMenu, setShowContextualMenu] = React.useState(false);
	const [settings, setSettings] = React.useState<SettingsShort[]>([]);
	const [isLoading, setIsLoading] = React.useState(false);

	const { accessToken } = React.useContext(AuthContext)
	const { tenantContext, setTenantContext } = React.useContext(TenantContext)
	const { graphContext } = React.useContext(GraphContext)
	const { userContext, setUserContext } = React.useContext(UserContext)

	const linkRef = React.useRef(null);
	const hasFetchedRef = React.useRef(false);
	const navigate = useNavigate();

	// --- Data fetching ---

	const fetchDomains = async () => {
		setIsLoading(true);
		try {
			const allDomains = await GetDomainExtensions(accessToken!, tenantContext.api);
			setSettings(allDomains);
		} catch {
			/* cancelled */
		} finally {
			setIsLoading(false);
		}
	};

	React.useEffect(() => {
		hasFetchedRef.current = false;
		setSettings([]);
	}, [tenantContext.api]);

	// --- Handlers ---

	const onShowContextualMenu = React.useCallback((ev: React.MouseEvent<HTMLElement>) => {
		ev.preventDefault();
		setShowContextualMenu(true);
		if (!hasFetchedRef.current && accessToken && tenantContext.api) {
			hasFetchedRef.current = true;
			fetchDomains();
		}
	}, [accessToken, tenantContext.api]);

	const onHideContextualMenu = React.useCallback(() => setShowContextualMenu(false), []);

	const handleChangeTenant = React.useCallback((_ev: React.FormEvent<HTMLDivElement>, item: IDropdownOption | undefined) => {
		if (!item || !accessToken) return
		setUserContext(prev => {
			const userData: UserAdminItem = item.key === 'clear'
				? { ...prev.userData, changeTenantEnabled: false, changeTenantDomain: '' }
				: { ...prev.userData, changeTenantEnabled: true, changeTenantDomain: item.key as string }
			updateUser(accessToken!, JSON.stringify(userData))
			return { ...prev, userData }
		})
		if (item.key !== 'clear') {
			navigate(`/prod/${item.key}/home`)
		}
	}, [accessToken, navigate, setUserContext])

	const handleClearChangeTenant = React.useCallback(() => {
		if (!accessToken) return
		setUserContext(prev => {
			const userData: UserAdminItem = { ...prev.userData, changeTenantEnabled: false, changeTenantDomain: '' }
			updateUser(accessToken, JSON.stringify(userData))
			const ownDomain = prev.userData.Domains?.[0]
			if (ownDomain) {
				navigate(`/${ownDomain.APIName.toLowerCase()}/${ownDomain.DomainName.toLowerCase()}/home`)
			}
			return { ...prev, userData }
		})
		setTenantContext(prev => ({ ...prev, simulatedGroups: undefined }))
	}, [accessToken, navigate, setUserContext, setTenantContext])

	const handleAdminToggle = React.useCallback((_ev: React.MouseEvent<HTMLElement>, checked?: boolean) => {
		localStorage.setItem('adminMode', checked!.toString())
		setUserContext(prev => ({ ...prev, adminMode: checked! }))
	}, [setUserContext])

	const handleSimulatedGroupsChange = React.useCallback((groups: IPersonaProps[]) => {
		const simulated = groups.map(g => ({
			groupID: (g.key as string) || tenantContext.companyGroups.find(cg => cg.displayName === g.text)?.id || '',
			groupName: g.text || '',
		}))
		setTenantContext(prev => ({
			...prev,
			simulatedGroups: simulated.length > 0 ? simulated : undefined,
		}))
	}, [setTenantContext, tenantContext.companyGroups])

	const handleClearSimulatedGroups = React.useCallback(() => {
		setTenantContext(prev => ({ ...prev, simulatedGroups: undefined }))
	}, [setTenantContext])

	// --- Memos ---

	const hasGroups = React.useMemo(() => {
		return GetUseAnyGroups(tenantContext.settings) && tenantContext.companyGroups.length > 0
	}, [tenantContext.settings, tenantContext.companyGroups])

	const changeTenantOptions: IDropdownOption[] = React.useMemo(() => {
		if (isLoading) return [{ key: 'loading', text: 'Loading...' }]
		if (!userContext.userData?.Domains) return []
		const domains = Array.isArray(userContext.userData.Domains)
			? userContext.userData.Domains.filter(d => d.APIName.toLowerCase() === 'prod')
			: []
		return [
			{ key: 'header', text: 'Change tenant', itemType: DropdownMenuItemType.Header },
			...domains.map((item: DomainItem) => ({
				key: item.DomainName,
				text: `${item.CompanyName} (${item.DomainName})`,
				ariaLabel: `${item.CompanyName} (${item.DomainName})`,
				data: Array.isArray(settings) ? settings.find(s => s.CompanyName === item.CompanyName) : undefined,
			} as IDropdownOption))
		]
	}, [userContext.userData, isLoading, settings]);

	const companyGroupPersonas: IPersonaProps[] = React.useMemo(() =>
		tenantContext.companyGroups.map(g => ({ text: g.displayName, key: g.id }))
	, [tenantContext.companyGroups])

	const selectedGroupPersonas: IPersonaProps[] = React.useMemo(() =>
		(tenantContext.simulatedGroups ?? []).map(g => ({ text: g.groupName, key: g.groupID }))
	, [tenantContext.simulatedGroups])

	const basePersona = React.useMemo(() => ({
		imageUrl: graphContext.userPhoto ? 'data:image/jpeg;base64,' + graphContext.userPhoto : undefined,
		text: graphContext.userDetails?.displayName,
		secondaryText: graphContext.userDetails?.userPrincipalName,
		tertiaryText: graphContext.userDetails?.jobTitle,
		imageAlt: "User Details",
	}), [graphContext.userPhoto, graphContext.userDetails]);

	// --- Render helpers ---

	const renderChangeTenantOption = (item: IDropdownOption | undefined) => {
		if (!item) return null
		return (
			<Stack horizontal tokens={{ childrenGap: 5 }} verticalAlign='center' horizontalAlign='space-between' styles={{ root: { width: '100%' } }}>
				<span>{item.text}</span>
				{(item.data as SettingsShort)?.UniqueManifestID && (
					<TooltipHost content='Denna kund har ett eget manifest - Kontakta utvecklingsavdelningen för mer hjälp'>
						<FontIcon iconName="DocumentManagement" />
					</TooltipHost>
				)}
			</Stack>
		)
	}

	const renderChangeTenantDomain = () => (
		<span style={{ color: '#a80000' }}>{userContext.userData?.changeTenantDomain}</span>
	)

	// --- Menu items ---

	const adminMenuItems: IContextualMenuItem[] = React.useMemo(() => {
		if (!userContext.userData?.isRehngruppen) return []

		const items: IContextualMenuItem[] = [
			{ key: 'divider_admin', itemType: ContextualMenuItemType.Divider },
			{
				key: 'adminMode',
				onRender: () => (
					<Toggle label="Admin" onText="On" offText="Off"
						checked={userContext.adminMode}
						onChange={handleAdminToggle}
						styles={MENU_MARGIN}
					/>
				)
			},
			{
				key: 'changeTenant',
				onRender: () => (
					<Stack styles={MENU_MARGIN}>
						<Dropdown
							placeholder="Change tenant"
							label="Change tenant"
							options={changeTenantOptions}
							onRenderOption={renderChangeTenantOption}
							selectedKey={userContext.userData?.changeTenantEnabled ? userContext.userData.changeTenantDomain : null}
							onChange={handleChangeTenant}
						/>
						{userContext.userData?.changeTenantEnabled && (
							<ActionButton iconProps={{ iconName: 'Cancel' }}
								text="Clear change tenant"
								onClick={handleClearChangeTenant}
							/>
						)}
					</Stack>
				)
			},
		]

		if (hasGroups) {
			items.push({
				key: 'simulateGroups',
				onRender: () => (
					<Stack styles={MENU_MARGIN}>
						<Label>Simulate groups</Label>
						<PeoplePickerGroups
							companyGroups={companyGroupPersonas}
							selectedGroups={selectedGroupPersonas}
							setSelectedGroups={handleSimulatedGroupsChange}
						/>
						{tenantContext.simulatedGroups && tenantContext.simulatedGroups.length > 0 && (
							<ActionButton iconProps={{ iconName: 'Cancel' }}
								text="Clear simulated groups"
								onClick={handleClearSimulatedGroups}
								styles={{ root: { marginTop: 5 } }}
							/>
						)}
					</Stack>
				)
			})
		}

		return items
	}, [userContext.userData, userContext.adminMode, changeTenantOptions, handleAdminToggle, handleChangeTenant, handleClearChangeTenant, hasGroups, companyGroupPersonas, selectedGroupPersonas, handleSimulatedGroupsChange, tenantContext.simulatedGroups, handleClearSimulatedGroups])

	const menuItems: IContextualMenuItem[] = React.useMemo(() => {
		if (!userContext.userData) return []
		return [
			{
				key: 'topbuttons',
				onRender: () => (
					<Stack horizontal horizontalAlign='space-between' verticalAlign='center' styles={{ root: { marginLeft: '10px' } }}>
						<Text>{userContext.userData.Domains && userContext.userData.Domains[0]?.CompanyName}</Text>
						<ActionButton text='Sign out' onClick={() => { props.logout(); navigate('/login'); }} />
					</Stack>
				)
			},
			{
				key: 'userinfo',
				onRender: () => (
					<Persona styles={{ root: { margin: '10px' } }}
						{...basePersona}
						size={PersonaSize.size72}
						{...userContext.userData?.changeTenantDomain && { onRenderTertiaryText: renderChangeTenantDomain }}
					/>
				)
			},
			...adminMenuItems
		]
	}, [userContext.userData, adminMenuItems, basePersona, props.logout, navigate])

	// --- Render ---

	return (
		<>
			<ContextualMenu
				items={menuItems}
				hidden={!showContextualMenu}
				target={linkRef}
				isBeakVisible={true}
				onItemClick={onHideContextualMenu}
				onDismiss={onHideContextualMenu}
			/>
			<div className='user-button' ref={linkRef} onClick={onShowContextualMenu}>
				<Stack.Item styles={{ root: { justifyContent: 'end', minWidth: 40, borderRadius: 20 } }}>
					<Persona className='ms-hiddenLgUp'
						imageUrl={graphContext.userPhoto ? 'data:image/jpeg;base64,' + graphContext.userPhoto : undefined}
						text='' secondaryText=''
						size={PersonaSize.size40}
						styles={{ root: { width: 40 } }}
					/>
					<Persona className='ms-hiddenMdDown'
						{...basePersona}
						size={PersonaSize.size40}
						{...userContext.userData?.changeTenantDomain && { onRenderSecondaryText: renderChangeTenantDomain }}
					/>
				</Stack.Item>
			</div>
		</>
	)
}

export default HeaderMenu;
